import React, { useState, useEffect } from "react";
import ErrorBoundary from "../../../Errors/ErrorBoundary";
import axiosInstance from "../../../axiosConfig";
import { Plus, Trash2, Save, X } from "lucide-react";

function ModuleForm({ type, moduleId, onClose, modules = [], onSuccess }) {
  const [moduleName, setModuleName] = useState("");
  const [moduleDescription, setModuleDescription] = useState("");
  const [moduleAccessId, setModuleAccessId] = useState("");
  const [moduleFeatures, setModuleFeatures] = useState([]);
  const [moduleAccessCodes, setModuleAccessCodes] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (type === "edit" && moduleId !== null) {
      const module = modules.find((mod) => mod.moduleId === moduleId);
      if (module) {
        setModuleName(module.moduleName || "");
        setModuleDescription(module.moduleDescription || "");
        setModuleAccessId(module.moduleAccessId || "");
        setModuleFeatures(
          Array.isArray(module.moduleFeatures)
            ? module.moduleFeatures.map((f) => ({
                moduleFeatureId: f.moduleFeatureId || 0,
                featureName: f.featureName || "",
                description: f.featureDescription || "",
              }))
            : [],
        );
        setModuleAccessCodes(
          Array.isArray(module.moduleFeatureAccessIds)
            ? module.moduleFeatureAccessIds.map((code) => ({
                moduleFeatureName: code.moduleFeatureName || "",
                featureAccessCode: module.moduleAccessId || "",
              }))
            : [],
        );
      }
    } else {
      setModuleName("");
      setModuleDescription("");
      setModuleAccessId("");
      setModuleFeatures([]);
      setModuleAccessCodes([{ moduleFeatureName: "", featureAccessCode: "" }]);
    }
  }, [type, moduleId, modules]);

  const handleFeatureChange = (index, event) => {
    const { name, value } = event.target;
    const updated = [...moduleFeatures];
    updated[index] = { ...updated[index], [name]: value };
    setModuleFeatures(updated);
  };

  const handleAccessCodeChange = (index, event) => {
    const { name, value } = event.target;
    const updated = [...moduleAccessCodes];
    updated[index] = { ...updated[index], [name]: value };
    setModuleAccessCodes(updated);
  };

  const addFeature = () =>
    setModuleFeatures([
      ...moduleFeatures,
      { moduleFeatureId: 0, featureName: "", description: "" },
    ]);
  const removeFeature = (index) =>
    setModuleFeatures(moduleFeatures.filter((_, i) => i !== index));
  const addAccessCode = () =>
    setModuleAccessCodes([
      ...moduleAccessCodes,
      { moduleFeatureName: "", featureAccessCode: "" },
    ]);
  const removeAccessCode = (index) =>
    setModuleAccessCodes(moduleAccessCodes.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (
      !moduleName.trim() ||
      !moduleDescription.trim() ||
      (type !== "edit" && !moduleAccessId.trim()) ||
      moduleFeatures.every((f) => !f.featureName.trim()) ||
      (type !== "edit" &&
        moduleAccessCodes.every(
          (c) => !c.moduleFeatureName.trim() || !c.featureAccessCode.trim(),
        ))
    ) {
      setError(
        "Module name, description, access ID, at least one feature, and access code fields are required.",
      );
      return;
    }

    const selectedProduct = JSON.parse(localStorage.getItem("selectedProduct"));
    const productId = selectedProduct?.productId;
    if (!productId) {
      setError("Product is not selected!");
      return;
    }

    const moduleData = {
      productId,
      moduleName,
      moduleDescription,
      moduleFeatures: moduleFeatures
        .filter((f) => f.featureName.trim())
        .map((f) => ({
          moduleFeatureId: f.moduleFeatureId,
          featureName: f.featureName,
          featureDescription: f.description,
        })),
    };

    if (type !== "edit") {
      moduleData.moduleAccessId = moduleAccessId;
      moduleData.moduleAccessCodes = moduleAccessCodes
        .filter((c) => c.moduleFeatureName.trim() && c.featureAccessCode.trim())
        .map((c) => ({
          moduleFeatureName: c.moduleFeatureName,
          featureAccessCode: c.featureAccessCode,
        }));
    }

    const url = `/modules${type === "edit" ? `/${moduleId}` : ""}`;
    try {
      setIsSubmitting(true);
      await axiosInstance({
        method: type === "edit" ? "put" : "post",
        url,
        data: moduleData,
        headers: { "Content-Type": "application/json" },
      });
      onSuccess();
      onClose();
    } catch (error) {
      setError(
        `An error occurred: ${error.response?.data?.message || error.message}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Shared feature row component
  const FeatureRow = ({ feature, index }) => (
    <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200">
      <div className="flex-1 grid grid-cols-2 gap-1.5">
        <input
          type="text"
          name="featureName"
          value={feature.featureName}
          onChange={(e) => handleFeatureChange(index, e)}
          placeholder="Feature name"
          className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
        <input
          type="text"
          name="description"
          value={feature.description}
          onChange={(e) => handleFeatureChange(index, e)}
          placeholder="Description"
          className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <button
        type="button"
        onClick={() => removeFeature(index)}
        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );

  return (
    <ErrorBoundary>
      <div className="w-full bg-white rounded-xl flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-bold text-gray-900">
              {type === "edit" ? "Edit Module" : "Add Module"}
            </h1>
            {type === "edit" && moduleName && (
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {moduleName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs">
              {error}
            </div>
          )}

          {type === "edit" ? (
            /* Edit Mode */
            <div className="space-y-3">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Module Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                    placeholder="Enter module name"
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Module Access ID
                  </label>
                  <input
                    type="text"
                    value={moduleAccessId}
                    disabled
                    placeholder="Read-only in edit mode"
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={moduleDescription}
                  onChange={(e) => setModuleDescription(e.target.value)}
                  placeholder="Enter module description"
                  rows={2}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Features <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline flex items-center gap-0.5"
                  >
                    <Plus size={12} /> Add Feature
                  </button>
                </div>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {moduleFeatures.map((feature, index) => (
                    <FeatureRow key={index} feature={feature} index={index} />
                  ))}
                  {moduleFeatures.length === 0 && (
                    <div className="text-center py-4 text-gray-400 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <p className="text-xs">
                        No features added. Click "Add Feature" to start.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Add Mode - 2 columns */
            <div className="grid grid-cols-2 gap-4">
              {/* Left — Basic Info + Features */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Module Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                    placeholder="Enter module name"
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Module Access ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={moduleAccessId}
                    onChange={(e) => setModuleAccessId(e.target.value)}
                    placeholder="Enter access ID"
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={moduleDescription}
                    onChange={(e) => setModuleDescription(e.target.value)}
                    placeholder="Enter description"
                    rows={2}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Features <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={addFeature}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline flex items-center gap-0.5"
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {moduleFeatures.map((feature, index) => (
                      <FeatureRow key={index} feature={feature} index={index} />
                    ))}
                    {moduleFeatures.length === 0 && (
                      <div className="text-center py-4 text-gray-400 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                        <p className="text-xs">No features added yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right — Access Codes */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Access Codes <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addAccessCode}
                    className="text-xs text-green-600 hover:text-green-700 font-medium hover:underline flex items-center gap-0.5"
                  >
                    <Plus size={12} /> Add
                  </button>
                </div>
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {moduleAccessCodes.map((code, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-green-50 p-2 rounded-lg border border-green-200"
                    >
                      <div className="flex-1 grid grid-cols-2 gap-1.5">
                        <input
                          type="text"
                          name="moduleFeatureName"
                          value={code.moduleFeatureName || ""}
                          onChange={(e) => handleAccessCodeChange(index, e)}
                          placeholder="Feature name"
                          className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500"
                        />
                        <input
                          type="text"
                          name="featureAccessCode"
                          value={code.featureAccessCode || ""}
                          onChange={(e) => handleAccessCodeChange(index, e)}
                          placeholder="Access code"
                          className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500 font-mono"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAccessCode(index)}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  {moduleAccessCodes.length === 0 && (
                    <div className="text-center py-4 text-gray-400 bg-green-50 rounded-lg border-2 border-dashed border-green-200">
                      <p className="text-xs">No access codes added yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-5 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                {type === "edit" ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>
                <Save size={14} />
                {type === "edit" ? "Update Module" : "Add Module"}
              </>
            )}
          </button>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default ModuleForm;
